import { useMemo } from 'react';
import {
	ROUTE_NAMES_RAPIDBOARD_USER_BOARD,
	ROUTE_NAMES_RAPIDBOARD_USER_BACKLOG,
	ROUTE_NAMES_RAPIDBOARD_USER_REPORT,
	ROUTE_NAMES_RAPIDBOARD_USER_REPORTS_OVERVIEW,
} from '@atlassian/jira-common-constants/src/spa-routes.tsx';
import { type Query, type Route, type Match, useRouter } from '@atlassian/react-resource-router';
import { useJiraUserBoardNavigationResource } from '../common/jsw-user-board-navigation-resource/index.tsx';
import { useJiraSoftwareProjectNavigation } from '../common/project-sidebar-navigation-resource/index.tsx';
import type { UseBoardReturn } from './types.tsx';
import { extractData, extractUserBoard } from './utils/extract-data.tsx';

const USER_BOARD_ROUTE_NAMES = [
	`${ROUTE_NAMES_RAPIDBOARD_USER_BOARD}`,
	`${ROUTE_NAMES_RAPIDBOARD_USER_BACKLOG}`,
	`${ROUTE_NAMES_RAPIDBOARD_USER_REPORT}`,
	`${ROUTE_NAMES_RAPIDBOARD_USER_REPORTS_OVERVIEW}`,
];

const isOnUserBoardRoute = (route: Route): boolean => USER_BOARD_ROUTE_NAMES.includes(route.name);

const isOnLocationLessBoardRoute = (match: Match, query: Query): boolean => {
	const { locationless = 'false' } = query;
	return match.url.includes('RapidBoard.jspa') && JSON.parse(locationless) === true;
};

export const useIsOnUserBoardOrLocationLessBoardRoute = (): boolean => {
	const [{ match, route, query }] = useRouter();
	return isOnUserBoardRoute(route) || isOnLocationLessBoardRoute(match, query);
};

export const useBoard = (): UseBoardReturn => {
	const isOnUserOrLocationlessBoardRoute = useIsOnUserBoardOrLocationLessBoardRoute();
	// pb -> project board
	const { data: pb, loading: pbLoading, error: pbError } = useJiraSoftwareProjectNavigation();
	// ub -> user board
	const { data: ub, loading: ubLoading, error: ubError } = useJiraUserBoardNavigationResource();

	const projectBoard = useMemo(() => pb && extractData(pb), [pb]);
	const userBoard = useMemo(() => ub && extractUserBoard(ub), [ub]);

	if (isOnUserOrLocationlessBoardRoute) {
		return { data: userBoard, loading: ubLoading, error: ubError };
	}
	return { data: projectBoard, loading: pbLoading, error: pbError };
};
